import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';

interface QueueItem {
  id: string;
  queueName: string;
  guestName: string;
  ticketNumber: string | null;
  orderNumber: string | null;
  location: string;
  status: string;
  position: number;
  createdAt: string;
}

interface GuestCard {
  fullName: string;
  tableName: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
  zoneName: string | null;
  suggestedEntrance: string | null;
  wayfindingInstructions: string | null;
}

interface DeliveredInfo {
  guestName: string;
  ticketNumber: string | null;
  tableName: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
  suggestedEntrance: string | null;
  wayfindingInstructions: string | null;
}

const QUEUES = ['Deep Wata Quay', 'Govna Wharf'];
const QUEUE_ACCENTS: Record<string, string> = {
  'Deep Wata Quay': '#38bdf8',
  'Govna Wharf': '#fbbf24',
};

export function DeliveryBoardPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [error, setError] = useState(false);
  const [clock, setClock] = useState<string>('');

  const [releasing, setReleasing] = useState<string | null>(null);
  const [delivered, setDelivered] = useState<DeliveredInfo | null>(null);

  const fetchQueue = useCallback(async () => {
    try {
      const data = await apiClient<QueueItem[]>('/packet-pickup/queue');
      setItems(Array.isArray(data) ? data : []);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  const handleRelease = useCallback(async (id: string) => {
    setReleasing(id);
    // Grab the item before we remove it so we can show its seat directions.
    const item = items.find((i) => i.id === id) || null;
    // Optimistically remove from the board
    setItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await apiClient(`/packet-pickup/queue/${id}/release`, { method: 'PATCH' });
    } catch {
      // On failure, refetch to restore accurate state
      fetchQueue();
    } finally {
      setReleasing(null);
    }

    // After releasing, show wayfinding to the guest's seat (best-effort).
    if (item?.ticketNumber) {
      try {
        const card = await apiClient<GuestCard>(`/guest-card/${encodeURIComponent(item.ticketNumber)}`);
        setDelivered({
          guestName: item.guestName,
          ticketNumber: item.ticketNumber,
          tableName: card.tableName,
          tableNumber: card.tableNumber,
          seatNumber: card.seatNumber,
          suggestedEntrance: card.suggestedEntrance,
          wayfindingInstructions: card.wayfindingInstructions,
        });
      } catch {
        // If lookup fails, still confirm the delivery without directions.
        setDelivered({
          guestName: item.guestName,
          ticketNumber: item.ticketNumber,
          tableName: null,
          tableNumber: null,
          seatNumber: null,
          suggestedEntrance: null,
          wayfindingInstructions: null,
        });
      }
    } else if (item) {
      setDelivered({
        guestName: item.guestName,
        ticketNumber: null,
        tableName: null,
        tableNumber: null,
        seatNumber: null,
        suggestedEntrance: null,
        wayfindingInstructions: null,
      });
    }
  }, [items, fetchQueue]);

  // Auto-dismiss the delivery directions banner after ~18 seconds.
  useEffect(() => {
    if (!delivered) return;
    const t = setTimeout(() => setDelivered(null), 18000);
    return () => clearTimeout(t);
  }, [delivered]);

  // Poll the queue every 8 seconds for the display board.
  useEffect(() => {
    fetchQueue();
    const t = setInterval(fetchQueue, 8000);
    return () => clearInterval(t);
  }, [fetchQueue]);

  // Live clock in the header.
  useEffect(() => {
    const update = () => setClock(new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, []);

  const grouped = QUEUES.map((q) => ({
    queueName: q,
    items: items
      .filter((i) => i.queueName === q || (!i.queueName && q === QUEUES[0]))
      .sort((a, b) => a.position - b.position),
  }));
  const total = items.length;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1vw' }}>
          <button
            onClick={() => navigate('/volunteer')}
            style={styles.backBtn}
            aria-label="Back to Volunteer Console"
            title="Back"
          >
            ← Back
          </button>
          <div style={styles.title}>📦 Packet Delivery Queue</div>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.totalPill}>{total} waiting</span>
          <span style={styles.clock}>{clock}</span>
        </div>
      </div>

      {error && (
        <div style={styles.errorBar}>Connection issue — retrying…</div>
      )}

      <div style={styles.columns}>
        {grouped.map((col) => (
          <div key={col.queueName} style={styles.column}>
            <div style={{ ...styles.columnHeader, color: QUEUE_ACCENTS[col.queueName] || '#fff', borderColor: QUEUE_ACCENTS[col.queueName] || '#fff' }}>
              {col.queueName}
              <span style={styles.colCount}>{col.items.length}</span>
            </div>
            {col.items.length === 0 ? (
              <div style={styles.empty}>No pickups waiting</div>
            ) : (
              <div style={styles.list}>
                {col.items.map((it, idx) => (
                  <div key={it.id} style={styles.row}>
                    <div style={styles.rowNum}>{idx + 1}</div>
                    <div style={styles.rowMain}>
                      <div style={styles.guestName}>{it.guestName}</div>
                      <div style={styles.rowMeta}>
                        {it.ticketNumber ? <span style={styles.ticket}>{it.ticketNumber}</span> : null}
                        {it.location ? <span style={styles.loc}>📍 {it.location}</span> : null}
                      </div>
                    </div>
                    <button
                      style={styles.releaseBtn}
                      onClick={() => handleRelease(it.id)}
                      disabled={releasing === it.id}
                      aria-label={`Mark ${it.guestName} delivered`}
                      title="Mark delivered"
                    >
                      {releasing === it.id ? '…' : '✓ Delivered'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {delivered && (() => {
        const seatBits: string[] = [];
        if (delivered.tableName) seatBits.push(delivered.tableName);
        if (delivered.seatNumber != null) seatBits.push(`Seat ${delivered.seatNumber}`);
        const fallback = [
          delivered.suggestedEntrance ? `Enter via ${delivered.suggestedEntrance}` : '',
          seatBits.join(' · '),
        ].filter(Boolean).join(' · ');
        const directions = delivered.wayfindingInstructions || fallback;
        return (
          <div style={styles.deliverBanner} role="status" aria-live="polite">
            <div style={styles.deliverInner}>
              <span style={styles.deliverBadge}>🧭 Deliver to</span>
              {(delivered.tableNumber != null || delivered.tableName) && (
                <div style={styles.tablePill}>
                  <span style={styles.tablePillLabel}>TABLE</span>
                  <span style={styles.tablePillValue}>
                    {delivered.tableNumber != null ? delivered.tableNumber : delivered.tableName}
                  </span>
                  {delivered.seatNumber != null && (
                    <span style={styles.tablePillSeat}>Seat {delivered.seatNumber}</span>
                  )}
                </div>
              )}
              <div style={styles.deliverText}>
                <span style={styles.deliverGuest}>
                  {delivered.guestName}
                  {delivered.ticketNumber ? <span style={styles.deliverTicket}> · {delivered.ticketNumber}</span> : null}
                </span>
                {delivered.tableName && (
                  <span style={styles.deliverTableName}>{delivered.tableName}</span>
                )}
                <span style={styles.deliverDirections}>
                  {directions || 'No seat directions on file for this guest.'}
                </span>
              </div>
            </div>
            <button
              style={styles.deliverClose}
              onClick={() => setDelivered(null)}
              aria-label="Dismiss directions"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        );
      })()}

      <div style={styles.footer}>Updates automatically · KDU Georgia 2026</div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    position: 'fixed', inset: 0, backgroundColor: '#0b1f16', color: '#fff',
    display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    padding: '2vh 2vw', boxSizing: 'border-box', overflow: 'hidden',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2vh', flexShrink: 0 },
  backBtn: { backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', padding: '0.5rem 1rem', fontSize: 'clamp(0.85rem, 1.5vw, 1.2rem)', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  title: { fontSize: 'clamp(1.6rem, 3.5vw, 3rem)', fontWeight: 900, letterSpacing: '0.02em' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '1.5vw' },
  totalPill: { fontSize: 'clamp(1rem, 2vw, 1.6rem)', fontWeight: 800, backgroundColor: '#166534', color: '#dcfce7', padding: '0.4rem 1.2rem', borderRadius: '999px' },
  clock: { fontSize: 'clamp(1rem, 2vw, 1.6rem)', fontWeight: 700, color: '#a7f3d0' },
  errorBar: { backgroundColor: '#7f1d1d', color: '#fecaca', textAlign: 'center', padding: '0.5rem', borderRadius: '8px', marginBottom: '1vh', fontSize: '1rem' },
  columns: { flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2vw', minHeight: 0 },
  column: { display: 'flex', flexDirection: 'column', backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '16px', padding: '1.5vh 1.2vw', minHeight: 0 },
  columnHeader: { fontSize: 'clamp(1.3rem, 2.6vw, 2.2rem)', fontWeight: 800, borderBottom: '3px solid', paddingBottom: '1vh', marginBottom: '1.5vh', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  colCount: { fontSize: 'clamp(1rem, 2vw, 1.6rem)', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '999px', padding: '0.1rem 0.9rem' },
  empty: { color: '#6b7280', fontSize: 'clamp(1rem, 2vw, 1.5rem)', textAlign: 'center', marginTop: '4vh' },
  list: { display: 'flex', flexDirection: 'column', gap: '1vh', overflowY: 'auto', flex: 1 },
  row: { display: 'flex', alignItems: 'center', gap: '1.2vw', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '1.2vh 1vw' },
  rowNum: { flexShrink: 0, width: 'clamp(2rem, 3.5vw, 3.2rem)', height: 'clamp(2rem, 3.5vw, 3.2rem)', borderRadius: '50%', backgroundColor: '#1a6b3c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 'clamp(1rem, 2vw, 1.6rem)' },
  rowMain: { minWidth: 0, flex: 1 },
  guestName: { fontSize: 'clamp(1.3rem, 2.8vw, 2.4rem)', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rowMeta: { display: 'flex', gap: '1.2vw', marginTop: '0.4vh', flexWrap: 'wrap' },
  ticket: { fontSize: 'clamp(0.9rem, 1.6vw, 1.3rem)', fontFamily: 'monospace', color: '#93c5fd', fontWeight: 700 },
  loc: { fontSize: 'clamp(0.9rem, 1.6vw, 1.3rem)', color: '#fcd34d', fontWeight: 600 },
  releaseBtn: { flexShrink: 0, backgroundColor: '#166534', color: '#dcfce7', border: '2px solid #22c55e', borderRadius: '10px', padding: 'clamp(0.5rem, 1.2vh, 1rem) clamp(0.8rem, 1.4vw, 1.4rem)', fontSize: 'clamp(0.9rem, 1.6vw, 1.4rem)', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' },
  footer: { flexShrink: 0, textAlign: 'center', color: '#4b5563', fontSize: '0.9rem', marginTop: '1vh' },
  deliverBanner: {
    flexShrink: 0, marginTop: '1.5vh', display: 'flex', alignItems: 'center', gap: '1.5vw',
    backgroundColor: '#166534', border: '3px solid #22c55e', borderRadius: '16px',
    padding: 'clamp(1rem, 2vh, 1.6rem) clamp(1rem, 2vw, 2rem)', boxShadow: '0 -4px 24px rgba(34,197,94,0.25)',
  },
  deliverInner: { display: 'flex', alignItems: 'center', gap: '1.5vw', flex: 1, minWidth: 0 },
  tablePill: { flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', color: '#0b1f16', borderRadius: '16px', padding: 'clamp(0.6rem, 1.2vh, 1.1rem) clamp(1rem, 1.8vw, 2rem)', minWidth: 'clamp(4.5rem, 8vw, 7rem)', lineHeight: 1 },
  tablePillLabel: { fontSize: 'clamp(0.65rem, 1.1vw, 1rem)', fontWeight: 800, letterSpacing: '0.1em', color: '#166534' },
  tablePillValue: { fontSize: 'clamp(2.2rem, 5vw, 4rem)', fontWeight: 900, color: '#0b1f16', margin: '0.1vh 0' },
  tablePillSeat: { fontSize: 'clamp(0.8rem, 1.4vw, 1.2rem)', fontWeight: 700, color: '#166534' },
  deliverBadge: { flexShrink: 0, fontSize: 'clamp(1rem, 2vw, 1.6rem)', fontWeight: 900, color: '#dcfce7', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '999px', padding: '0.4rem 1.2rem', whiteSpace: 'nowrap' },
  deliverText: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
  deliverGuest: { fontSize: 'clamp(1.3rem, 2.8vw, 2.2rem)', fontWeight: 900, color: '#fff' },
  deliverTicket: { fontFamily: 'monospace', color: '#a7f3d0', fontWeight: 700 },
  deliverTableName: { fontSize: 'clamp(1.6rem, 3.4vw, 2.8rem)', fontWeight: 900, color: '#fff', margin: '0.2vh 0', textTransform: 'uppercase', letterSpacing: '0.01em' },
  deliverDirections: { fontSize: 'clamp(1.1rem, 2.2vw, 1.8rem)', fontWeight: 700, color: '#dcfce7', marginTop: '0.3vh' },
  deliverClose: { flexShrink: 0, backgroundColor: 'rgba(0,0,0,0.25)', color: '#fff', border: 'none', borderRadius: '10px', width: 'clamp(2.2rem, 3.5vw, 3rem)', height: 'clamp(2.2rem, 3.5vw, 3rem)', fontSize: 'clamp(1rem, 2vw, 1.5rem)', fontWeight: 900, cursor: 'pointer' },
};
